import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'components/common'
import CsvFileUploader, {
  CSV_FILE_UPLOAD_CONFIG,
} from 'components/fileUploader/fileUploader'
import ImportModal, { ImportColumn } from 'components/common/importModal'
import Modal from 'components/modal'
import { usePresignedUpload } from 'hooks'
import { importBundleProducts } from 'utils/api/bundleProductApi'
import { downloadImportSample, getImportTemplate } from 'utils/api/importApi'
import { handleError } from 'utils/helpers/handleError'

const BUNDLE_PRODUCT_IMPORT_MODULE = 'bundleProduct'
const BUNDLE_PRODUCT_UPLOAD_MODULE = 'BUNDLE_PRODUCT_IMPORT'

interface BundleProductImportModalProps {
  isOpen: boolean
  closeModal: () => void
}

const BundleProductImportModal = ({
  isOpen,
  closeModal,
}: BundleProductImportModalProps) => {
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const [showColumnsModal, setShowColumnsModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string>()

  const { uploadProgress, isUploading, uploadAndProcess } = usePresignedUpload({
    module: BUNDLE_PRODUCT_UPLOAD_MODULE,
    onProcess: (key) => importBundleProducts(key),
  })

  useEffect(() => {
    if (!isOpen) return
    setShowColumnsModal(true)

    getImportTemplate(BUNDLE_PRODUCT_IMPORT_MODULE)
      .then((res) => setImportColumns(res.data.columns))
      .catch(() => {})
  }, [isOpen])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample(BUNDLE_PRODUCT_IMPORT_MODULE)
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'bundle_product_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const resetAndClose = () => {
    setShowColumnsModal(false)
    setShowUploadModal(false)
    setFile(null)
    setFileError(undefined)
    closeModal()
  }

  const handleClose = () => {
    if (isUploading) return
    resetAndClose()
  }

  const validateFile = (selected: File | null): string | undefined => {
    if (!selected) return undefined

    const isCsvExt = selected.name.toLowerCase().endsWith('.csv')
    if (
      !CSV_FILE_UPLOAD_CONFIG.allowedMimeTypes.includes(selected.type) ||
      !isCsvExt
    ) {
      return 'Only CSV files are allowed'
    }

    if (selected.size > CSV_FILE_UPLOAD_CONFIG.maxSize) {
      return `File size exceeds ${CSV_FILE_UPLOAD_CONFIG.maxMB}MB limit`
    }

    return undefined
  }

  const handleImport = async () => {
    if (!file) {
      setFileError('Please select a CSV file to import')
      return
    }

    const toastId = toast.loading('Uploading file...')
    try {
      await uploadAndProcess(file)
      toast.success(
        'Bundle product import has been queued. You will receive an email once it completes.',
        { id: toastId, duration: 5000 },
      )
      resetAndClose()
    } catch (error) {
      handleError(error, toastId)
    }
  }

  return (
    <>
      <ImportModal
        isOpen={isOpen && showColumnsModal}
        onClose={handleClose}
        onConfirm={() => {
          setShowColumnsModal(false)
          setShowUploadModal(true)
        }}
        moduleName="Bundle Products"
        columns={importColumns}
        onDownloadSample={handleDownloadSample}
      />

      <Modal
        isOpen={isOpen && showUploadModal}
        closeModal={handleClose}
        title="Import Bundle Products"
        width={480}
      >
        <div className="space-y-6 py-2">
          <CsvFileUploader
            label="Upload Bundle Product CSV"
            selectedFileName={file?.name}
            uploadProgress={uploadProgress}
            error={fileError}
            config={CSV_FILE_UPLOAD_CONFIG}
            onFileSelect={(selected) => {
              const error = validateFile(selected)
              setFile(error ? null : selected)
              setFileError(error)
            }}
          />

          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="text-sm text-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              buttonText={isUploading ? 'Uploading...' : 'Import'}
              onClick={handleImport}
              disabled={isUploading}
              loading={isUploading}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default BundleProductImportModal
